import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { X } from 'lucide-react';

const CookieConsent = () => {
  // Initialize state from localStorage using lazy initializers
  const [showBanner, setShowBanner] = useState(() => {
    const consent = localStorage.getItem('jfi_cookie_consent');
    return !consent;
  });
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState(() => {
    const consent = localStorage.getItem('jfi_cookie_consent');
    if (consent) {
      return JSON.parse(consent);
    }
    return {
      essential: true,
      analytics: false,
      marketing: false
    };
  });

  const savePreferences = (prefs) => {
    localStorage.setItem('jfi_cookie_consent', JSON.stringify(prefs));
    setPreferences(prefs);
    setShowBanner(false);
    setShowSettings(false);
    
    // Dispatch custom event for AnalyticsManager to handle GA4 loading
    window.dispatchEvent(new CustomEvent('jfi-consent-change', {
      detail: {
        analytics: prefs.analytics,
        marketing: prefs.marketing,
        essential: prefs.essential
      }
    }));
  };

  const acceptAll = () => {
    savePreferences({ essential: true, analytics: true, marketing: true });
  };

  const rejectNonEssential = () => {
    savePreferences({ essential: true, analytics: false, marketing: false });
  };

  const openSettings = () => {
    setShowSettings(true);
  };

  const saveSettings = () => {
    savePreferences(preferences);
  };

  // Expose function to reopen settings from footer
  useEffect(() => {
    window.openCookieSettings = () => {
      setShowSettings(true);
    };
    return () => {
      delete window.openCookieSettings;
    };
  }, []);

  return (
    <>
      {/* Cookie Banner - only show if consent not yet given */}
      {showBanner && (
        <div 
          className="fixed bottom-0 left-0 right-0 bg-white border-t border-neutral-200 shadow-lg z-50"
          role="dialog"
          aria-label="Cookie consent banner"
          data-testid="cookie-banner"
        >
          <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-[1180px] py-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex-1">
                <p className="text-sm text-neutral-700">
                  We use cookies to make our site work properly and to understand how visitors use it. You can choose what you allow.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button 
                  size="sm" 
                  className="bg-cta hover:bg-cta-hover transition-all duration-200"
                  onClick={acceptAll}
                  style={{fontSize: '14px'}}
                  data-testid="accept-all-cookies"
                >
                  Accept All Cookies
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  className="border border-neutral-300 hover:bg-neutral-100"
                  onClick={rejectNonEssential}
                  style={{fontSize: '14px'}}
                  data-testid="reject-cookies"
                >
                  Reject Non-Essential
                </Button>
                <Button 
                  size="sm" 
                  variant="ghost"
                  onClick={openSettings}
                  style={{fontSize: '14px'}}
                  data-testid="cookie-settings-btn"
                >
                  Cookie Settings
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Settings Dialog - always render so it can be opened from footer */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Cookie Preferences
            </DialogTitle>
            <DialogDescription>
              Manage your cookie preferences. Essential cookies are always enabled.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 pt-4">
            {/* Essential Cookies */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h4 className="font-semibold mb-1">
                  Essential Cookies
                </h4>
                <p className="text-sm text-neutral-600">
                  Required for the website to function properly. Cannot be disabled.
                </p>
              </div>
              <Switch checked={true} disabled />
            </div>

            {/* Analytics Cookies */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h4 className="font-semibold mb-1">
                  Analytics Cookies
                </h4>
                <p className="text-sm text-neutral-600">
                  Help us understand how visitors use our website (Google Analytics 4).
                </p>
              </div>
              <Switch 
                checked={preferences.analytics}
                onCheckedChange={(checked) => setPreferences({...preferences, analytics: checked})}
              />
            </div>

            {/* Marketing Cookies */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h4 className="font-semibold mb-1">
                  Marketing Cookies
                </h4>
                <p className="text-sm text-neutral-600">
                  Used for advertising and remarketing (not currently in use).
                </p>
              </div>
              <Switch 
                checked={preferences.marketing}
                onCheckedChange={(checked) => setPreferences({...preferences, marketing: checked})}
              />
            </div>
          </div>

          <div className="flex gap-3 justify-end pt-6">
            <Button 
              variant="outline" 
              onClick={() => setShowSettings(false)}
                         >
              Cancel
            </Button>
            <Button 
              className="bg-cta hover:bg-cta-hover"
              onClick={saveSettings}
                         >
              Save Preferences
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsent;
